<template>
	<tr>
		<td class="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
			<span v-if="thread">{{ __("Bid Placed") }}</span>
			<span v-else>
				<ui-check-group
					:form="form"
					:field="'matches.' + match.id + '.input.placeBid'"
					v-model="checked"
					label="New"
					@change="updateSuggestedPrice"
				/>
			</span>
		</td>
		<td class="px-3 py-3 text-sm text-gray-500">
			{{
				$cropOptions.getLocalizedLabel(
					match.user.type,
					$cropOptions.companyTypeOptions
				)
			}}
		</td>
		<td class="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
			{{
				$cropOptions.getLocalizedLabel(
					match.product.grade,
					$cropOptions.productGradeOptions
				)
			}}
			<br />
			{{ match.product.harvest }}
		</td>
		<td class="px-3 py-3 text-sm text-gray-500">
			{{ match.location.address.city }}
		</td>
		<td class="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
			<span v-if="match.distance_road_km > 10">
				{{ Number(match.distance_road_km).toFixed(0) }} km
			</span>
			<span v-else-if="match.distance_road_km !== null && match.distance_road_km < 10">
				< 10 km
			</span>
		</td>
		<td class="px-3 py-3 whitespace-nowrap text-sm text-gray-500" v-if="!thread">
			<ui-input-group
				suffix="€/t"
				:form="form"
				:field="'matches.' + match.id + '.freight_cost_eur'"
				v-model="match.freight_cost_eur"
				:dp="2"
				:noLabel="true"
				:required="true"
				:disabled="!checked"
				@input="updateSuggestedPrice"
			/>
		</td>
		<td class="px-3 py-3 whitespace-nowrap text-sm text-gray-500" v-if="!thread">
			<ui-input-group
				suffix="t"
				:form="form"
				:field="'matches.' + match.id + '.input.qty__mt'"
				v-if="match.qty.partial_ok"
				class="min-w-[5rem]"
				v-model="qty__mt"
				:dp="0"
				:noLabel="true"
				:disabled="!checked"
				:required="true"
				@input="updateSuggestedPrice"
			/>
			<span v-else class="flex items-center">
				{{ match.qty.mt }} t
				<ui-tooltip class="ml-2" :text="__('Partial quantity not desired')" />
			</span>
		</td>
		<td
			class="px-3 py-3 whitespace-nowrap text-sm text-gray-500"
			v-if="checked && !thread"
		>
			<ui-input-group
				suffix="€/t"
				:form="form"
				:field="'matches.' + match.id + '.input.price__mt'"
				class="min-w-[7rem]"
				v-model="price__mt"
				:key="'IG_MATCH_PRICE_MT_' + match.id + '__' + bid.price__mt"
				:noLabel="true"
				:required="true"
				:disabled="true"
				:dp="2"
				v-if="offer.price.type == 'FLAT'"
			/>
			<ui-input-group
				suffix="€/t"
				:form="form"
				:field="'matches.' + match.id + '.input.price__basis'"
				class="min-w-[7rem]"
				v-model="price__basis"
				:key="'IG_MATCH_PRICE_BASIS_' + match.id + '__' + bid.price__basis"
				:noLabel="true"
				:required="true"
				:disabled="true"
				:dp="2"
				v-else-if="offer.price.type == 'BASIS'"
			/>
			<ui-input-group
				suffix="€/t"
				:form="form"
				:field="'matches.' + match.id + '.input.price__basis'"
				class="min-w-[7rem]"
				v-model="price__basis"
				:key="'IG_MATCH_PRICE_INDEXED_' + match.id + '__' + bid.price__basis"
				:noLabel="true"
				:required="true"
				:disabled="true"
				:dp="2"
				v-else-if="offer.price.type == 'INDEXED'"
			/>
		</td>
		<td
			class="px-3 py-3 whitespace-nowrap text-sm text-gray-500"
			v-if="checked && !thread && ['INDEXED', 'BASIS'].includes(offer.price.type)"
		>
			<ui-input-group
				suffix="€/t"
				:form="form"
				:field="'matches.' + match.id + '.input.price__basis'"
				class="min-w-[7rem]"
				v-model="currentPriceIndication"
				:key="
					'IG_MATCH_PRICE_INDEXED_CURRENT_INDICATION' +
					match.id +
					'__' +
					bid.price__basis
				"
				:noLabel="true"
				:required="true"
				:disabled="true"
				:dp="2"
			/>
		</td>
		<td
			class="px-3 py-3 text-sm text-gray-500"
			v-if="thread && lastTradable"
		>
			<div class="flex flex-col gap-y-1 min-w-[7rem]">
				<span
					>Status:
					{{
						__(
							$cropOptions.getLabel(
								thread.status,
								$cropOptions.threadStatusOptions
							)
						)
					}}</span
				>

				<ui-button
					class="flex gap-x-1 items-center px-2"
					type="secondary"
					v-on:click="$inertia.visit($route('threads', { active_id: thread.id }))"
				>
					{{ __("To negotiation") }}
				</ui-button>
			</div>
		</td>
		<td
			class="px-3 py-3 whitespace-nowrap text-sm text-gray-500"
			v-if="thread && lastTradable"
		>
			<div class="min-w-[7rem]">{{ lastTradable.qty__mt }} t</div>
		</td>

		<td
			class="px-3 py-3 whitespace-nowrap text-sm text-gray-500"
			v-if="thread && lastTradable"
		>
			<div class="min-w-[7rem]">
				<span v-if="lastTradable.price__type == 'FLAT'">
					{{ lastTradable.price__mt }} €/t
				</span>
				<span v-else-if="lastTradable.price__type == 'INDEXED'">
					Basis:
					<br />
					{{ lastTradable.price__basis }} €/t
				</span>
			</div>
		</td>
		<td
			class="px-3 py-3 whitespace-nowrap text-sm text-gray-500"
			v-if="thread && lastTradable && lastTradable.price__type == 'INDEXED'"
		>
			<div class="min-w-[7rem]">{{ lastTradable.price__mt }} €/t</div>
		</td>
	</tr>
</template>

<script>
export default {
	props: {
		offer: {
			type: Object,
			required: true,
		},
		match: {
			type: Object,
			required: true,
		},
		// NUR Error Provider für kommende Validierung!
		form: {
			type: Object,
			required: true,
		},
		bid: {
			type: Object,
			default: () => ({placeBid: false}),
		},
	},
	data() {

		return {
			qty__mt: this.bid?.qty__mt ?? this.match.qty.mt,
			price__type: this.bid?.price__type ?? this.offer.price.type,
			price__basis: this.bid?.price__basis ?? null,
			price__mt: this.bid?.price__mt ?? null,
			checked: this.bid?.placeBid ?? false,
			input: this.match.input,
		};
	},
	computed: {
		thread() {
				return this.match.thread;
		},
		lastTradable() {
				return this.match.lastTradable;
		},
		currentPriceIndication() {
			let cf = this.offer.commodityFuture;
			if (cf) {
				let price = 1 * cf.euronext_price + 1 * this.bid.price__basis;
				return price.toFixed(2);
			} else {
				return "";
			}
		},
	},
	methods: {
		updateSuggestedPrice() {

			let newPrice = null;
			let newBasisPrice = null;

			if (this.offer.price.type === "FLAT") {
				newPrice = this.offer.price.mt - this.match.freight_cost_eur;
				if(newPrice){
					newPrice = newPrice.toFixed(2);
				}
			} else if (['INDEXED', 'BASIS'].includes(this.offer.price.type)) {
				// kein papiermarkt, basis ist hier nicht möglich -.-
				newBasisPrice = this.offer.price.basis - this.match.freight_cost_eur;
				if(newBasisPrice){
					newBasisPrice = newBasisPrice.toFixed(2);
				}
			}

			this.$emit('update-match', {
				id: this.match.id,
				input: {
					placeBid: this.checked,
					price__mt: newPrice,
					price__basis: newBasisPrice,
					trade: this.match.trade,
					qty__mt: this.qty__mt ?? this.match.qty.mt,
				}
			});

		},
	},
	watch: {
		bid: {
			handler(newData) {
				this.price__mt = newData?.price__mt ?? null;
				this.price__basis = newData?.price__basis ?? null;
				this.qty__mt = newData?.qty__mt ?? null;
			},
			deep: true,
			immediate: true
		}
	},
};
</script>
